import { Link, useParams } from 'react-router-dom';
import {
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import Navbar from '../../components/Navbar';
import Chatbot from '../../components/Chatbot';

export default function FlowNamingPage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to={`${basePath}/help`} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            ← Back to Help Center
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-4">Flow Naming Conventions</h1>
          <p className="text-lg text-gray-600 mt-2">
            Best practices for naming flows so they're easy to find and understand
          </p>
        </div>

        {/* Info Alert */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
          <div className="flex">
            <InformationCircleIcon className="h-6 w-6 text-blue-400 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-800">Naming Best Practice</h3>
              <p className="mt-2 text-sm text-blue-700">
                Good flow names make it instantly clear what object the flow affects, what action it performs,
                and when it runs - without having to open the flow.
              </p>
            </div>
          </div>
        </div>

        {/* What makes a good name */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What Makes a Good Flow Name?</h2>
          <p className="text-gray-700 mb-4">
            A well-named flow should answer three questions at a glance:
          </p>

          <div className="space-y-3">
            <div className="flex items-start bg-blue-50 p-3 rounded">
              <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold mr-3">
                1
              </span>
              <div>
                <h3 className="font-semibold text-blue-900">What object?</h3>
                <p className="text-sm text-blue-800">Which Salesforce object does this flow work with?</p>
              </div>
            </div>

            <div className="flex items-start bg-green-50 p-3 rounded">
              <span className="flex-shrink-0 h-6 w-6 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold mr-3">
                2
              </span>
              <div>
                <h3 className="font-semibold text-green-900">What action?</h3>
                <p className="text-sm text-green-800">What does the flow do?</p>
              </div>
            </div>

            <div className="flex items-start bg-purple-50 p-3 rounded">
              <span className="flex-shrink-0 h-6 w-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold mr-3">
                3
              </span>
              <div>
                <h3 className="font-semibold text-purple-900">When does it run?</h3>
                <p className="text-sm text-purple-800">What triggers the flow?</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recommended naming pattern */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Recommended Naming Pattern</h2>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
            <div className="flex">
              <LightBulbIcon className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-green-800">Standard Pattern</h3>
                <p className="mt-2 text-sm font-mono text-green-700">
                  [Object]_[Action]_[Trigger/Context]
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded">
              <p className="text-sm font-semibold text-gray-900 mb-2">Examples of Good Names:</p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <div>
                    <span className="font-mono text-blue-600">Account_Update_BillingAddress_OnSave</span>
                    <p className="text-gray-600 text-xs mt-1">Clear: Updates Account billing address when saved</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <div>
                    <span className="font-mono text-blue-600">Opportunity_Create_RenewalOpp_BeforeCloseDate</span>
                    <p className="text-gray-600 text-xs mt-1">Clear: Creates renewal opportunity before close date</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <div>
                    <span className="font-mono text-blue-600">Lead_Assign_Territory_OnStatusChange</span>
                    <p className="text-gray-600 text-xs mt-1">Clear: Assigns territory when lead status changes</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <div>
                    <span className="font-mono text-blue-600">Case_Send_EscalationEmail_AfterSLA</span>
                    <p className="text-gray-600 text-xs mt-1">Clear: Sends escalation email when SLA is breached</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-red-50 p-4 rounded">
              <p className="text-sm font-semibold text-red-900 mb-2">Examples of Poor Names:</p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✗</span>
                  <div>
                    <span className="font-mono text-gray-600">UpdateFlow</span>
                    <p className="text-gray-600 text-xs mt-1">Too vague: Updates what? When?</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✗</span>
                  <div>
                    <span className="font-mono text-gray-600">Account Management</span>
                    <p className="text-gray-600 text-xs mt-1">Too broad: What specific action?</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✗</span>
                  <div>
                    <span className="font-mono text-gray-600">Flow_v2</span>
                    <p className="text-gray-600 text-xs mt-1">Meaningless: Tells nothing about purpose</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✗</span>
                  <div>
                    <span className="font-mono text-gray-600">JohnsFlow</span>
                    <p className="text-gray-600 text-xs mt-1">Not descriptive: What does it do?</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Naming components */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Naming Components Guide</h2>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">1. Object Name (Required)</h3>
              <p className="text-sm text-gray-700 mb-2">Start with the primary object the flow affects</p>
              <div className="bg-gray-50 p-3 rounded">
                <ul className="text-sm text-gray-700 space-y-1">
                  <li><span className="font-mono">Account_</span></li>
                  <li><span className="font-mono">Opportunity_</span></li>
                  <li><span className="font-mono">Contact_</span></li>
                  <li><span className="font-mono">Lead_</span></li>
                  <li><span className="font-mono">Case_</span></li>
                  <li><span className="font-mono">CustomObject__c_</span></li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">2. Action Verb (Required)</h3>
              <p className="text-sm text-gray-700 mb-2">Describe what the flow does</p>
              <div className="bg-gray-50 p-3 rounded">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Common Actions:</p>
                    <ul className="text-gray-700 space-y-1">
                      <li><span className="font-mono">Create_</span> - Creates records</li>
                      <li><span className="font-mono">Update_</span> - Updates fields</li>
                      <li><span className="font-mono">Delete_</span> - Deletes records</li>
                      <li><span className="font-mono">Assign_</span> - Assigns ownership/values</li>
                      <li><span className="font-mono">Calculate_</span> - Performs calculations</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">More Actions:</p>
                    <ul className="text-gray-700 space-y-1">
                      <li><span className="font-mono">Send_</span> - Sends emails/notifications</li>
                      <li><span className="font-mono">Sync_</span> - Synchronizes data</li>
                      <li><span className="font-mono">Validate_</span> - Validates data</li>
                      <li><span className="font-mono">Archive_</span> - Archives old records</li>
                      <li><span className="font-mono">Notify_</span> - Creates notifications</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">3. Specific Details (Required)</h3>
              <p className="text-sm text-gray-700 mb-2">What specific field or process?</p>
              <div className="bg-gray-50 p-3 rounded text-sm">
                <ul className="text-gray-700 space-y-1">
                  <li><span className="font-mono">_TerritoryOwner</span> - Which field is affected</li>
                  <li><span className="font-mono">_RenewalOpportunity</span> - What is being created</li>
                  <li><span className="font-mono">_WelcomeEmail</span> - What is being sent</li>
                  <li><span className="font-mono">_AccountStatus</span> - What is being calculated</li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">4. Trigger/Context (Recommended)</h3>
              <p className="text-sm text-gray-700 mb-2">When or why the flow runs</p>
              <div className="bg-gray-50 p-3 rounded text-sm">
                <ul className="text-gray-700 space-y-1">
                  <li><span className="font-mono">_OnCreate</span> - When record is created</li>
                  <li><span className="font-mono">_OnSave</span> - After record is saved</li>
                  <li><span className="font-mono">_OnStatusChange</span> - When specific field changes</li>
                  <li><span className="font-mono">_Daily</span> - Scheduled daily</li>
                  <li><span className="font-mono">_BeforeDelete</span> - Before record deletion</li>
                  <li><span className="font-mono">_AfterClose</span> - After specific stage</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Special cases */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Special Cases</h2>

          <div className="space-y-4">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-gray-900">Screen Flows</h3>
              <p className="text-gray-700 text-sm mt-1 mb-2">
                Include "Screen" and the process name
              </p>
              <p className="font-mono text-sm text-blue-600">Screen_NewCaseSubmission</p>
              <p className="font-mono text-sm text-blue-600">Screen_QuoteGenerator</p>
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="font-semibold text-gray-900">Multi-Object Flows</h3>
              <p className="text-gray-700 text-sm mt-1 mb-2">
                List primary object first, then secondary
              </p>
              <p className="font-mono text-sm text-green-600">Account_Contact_SyncBillingAddress_OnSave</p>
              <p className="font-mono text-sm text-green-600">Opportunity_Product_ApplyDiscount_OnCreate</p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="font-semibold text-gray-900">Scheduled Flows</h3>
              <p className="text-gray-700 text-sm mt-1 mb-2">
                Include frequency in the name
              </p>
              <p className="font-mono text-sm text-purple-600">Account_Archive_InactiveAccounts_Monthly</p>
              <p className="font-mono text-sm text-purple-600">Lead_Update_Score_Daily</p>
            </div>

            <div className="border-l-4 border-orange-500 pl-4">
              <h3 className="font-semibold text-gray-900">Subflows</h3>
              <p className="text-gray-700 text-sm mt-1 mb-2">
                Prefix with "Sub_" to indicate it's called by other flows
              </p>
              <p className="font-mono text-sm text-orange-600">Sub_SendNotificationEmail</p>
              <p className="font-mono text-sm text-orange-600">Sub_ValidateAddress</p>
            </div>
          </div>
        </div>

        {/* Before/After */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Before & After Example</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Before */}
            <div className="border-2 border-red-300 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <XCircleIcon className="h-6 w-6 text-red-500 mr-2" />
                <h3 className="font-semibold text-red-700">Before (Poor Name)</h3>
              </div>
              <div className="space-y-3">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="font-mono text-sm text-gray-900">UpdateFlow2</p>
                </div>
                <p className="text-sm text-red-700">
                  Questions left unanswered:
                </p>
                <ul className="text-sm text-red-700 space-y-1 ml-4">
                  <li>• What object does it update?</li>
                  <li>• What fields does it update?</li>
                  <li>• When does it run?</li>
                  <li>• Why is there a "2"?</li>
                </ul>
              </div>
            </div>

            {/* After */}
            <div className="border-2 border-green-300 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                <h3 className="font-semibold text-green-700">After (Good Name)</h3>
              </div>
              <div className="space-y-3">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="font-mono text-sm text-gray-900">
                    Opportunity_Update_RenewalDate_BeforeClose
                  </p>
                </div>
                <p className="text-sm text-green-700">
                  Immediately clear:
                </p>
                <ul className="text-sm text-green-700 space-y-1 ml-4">
                  <li>• ✓ Updates Opportunity object</li>
                  <li>• ✓ Updates Renewal Date field</li>
                  <li>• ✓ Runs before close date</li>
                  <li>• ✓ Purpose is obvious</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Best practices */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Naming Best Practices</h2>

          <div className="space-y-4">
            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Use Underscores, Not Spaces</h3>
                <p className="text-gray-700 text-sm mt-1">
                  <span className="font-mono text-green-600">Account_Update</span> instead of
                  <span className="font-mono text-gray-400 line-through ml-1">Account Update</span>
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Use PascalCase or Title_Case</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Capitalize each word for readability
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Be Specific, Not Generic</h3>
                <p className="text-gray-700 text-sm mt-1">
                  "Update_BillingAddress" is better than "Update_Fields"
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Avoid Version Numbers</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Use descriptive names instead of "_v2" or "_Final". Salesforce tracks versions automatically.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Keep it Under 80 Characters</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Long names get truncated in lists. Aim for descriptive but concise.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Establish Team Conventions</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Agree on a naming pattern with your team and document it for consistency
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Impact */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
          <h2 className="text-xl font-bold text-blue-900 mb-2">Expected Benefits</h2>
          <div className="text-blue-800 space-y-2">
            <p className="font-semibold">Productivity: Significantly improved</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Find the right flow 5x faster in Setup</li>
              <li>Understand flow purpose without opening it</li>
              <li>Onboard new team members faster</li>
              <li>Prevent duplicate automations</li>
              <li>Better searchability and organization</li>
            </ul>
          </div>
        </div>

        {/* Related Resources */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Related Resources</h2>
          <ul className="space-y-3">
            <li>
              <Link to={`${basePath}/help/flow-description`} className="text-blue-600 hover:text-blue-800 font-medium">
                Adding Flow Descriptions →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Document what your flows do</p>
            </li>
            <li>
              <Link to={`${basePath}/help/best-practices`} className="text-blue-600 hover:text-blue-800 font-medium">
                Flow Best Practices →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Complete guide to building high-quality flows</p>
            </li>
            <li>
              <Link to={`${basePath}/help/flow-complexity`} className="text-blue-600 hover:text-blue-800 font-medium">
                Managing Flow Complexity →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Keeping flows maintainable</p>
            </li>
          </ul>
        </div>
      </div>

      <Chatbot />
    </div>
  );
}
